import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography, TextField, Button } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import { aiAgent } from "store/actions";
import { useSelector } from "react-redux";
import TypingIndicator from "./typingIndicator";
import {
  DetailBox,
  LoadingContainer,
  MessageListContainer,
  motionDivStyle,
  StyledButton,
  StyledIconButton,
  StyledPaper,
  StyledText,
} from "./styles";

const initialState = [{ sender: "bot", text: "Hello, how can I help you?" }];
const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialState);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState(null);
  const scrollRef = useRef(null);
  const userData = useSelector((state) => state.auth?.authData);
  const practiceDetails = useSelector(
    (state) => state.auth.authData?.practice_details
  );
  const practiceList = useSelector((state) => state.auth?.authData?.practices);

  const selectedPracticeDetails = practiceList?.find(
    (item) => item?.practice_id === practiceDetails?.practice_id
  );

  const toggleChat = () => setOpen(!open);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await aiAgent({ user_prompt: input });

      const botMessage = {
        sender: "bot",
        text: response.response || "Sorry, I didn't get that.",
        references: response.references,
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.log("err sendMessage: ", err);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Failed to get response. Try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    setMessages(initialState);
  }, [userData]);

  if (
    userData &&
    userData.access_token &&
    selectedPracticeDetails?.source_entity === "Orci Care"
  ) {
    return (
      <>
        {/* Floating Button */}
        <Box position="fixed" bottom={50} right={30} zIndex={1300}>
          <StyledIconButton onClick={toggleChat}>
            <ChatIcon color="secondary" />
          </StyledIconButton>
        </Box>

        {/* Chat Window */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
              style={motionDivStyle}
            >
              <StyledPaper elevation={4}>
                {/* Header */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Chat
                  </Typography>
                  <IconButton onClick={toggleChat} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Message List */}
                <MessageListContainer ref={scrollRef}>
                  {messages.map((msg, idx) => {
                    const hasDetails =
                      msg.references && msg.references.length > 0;

                    // Function to toggle showing details
                    const toggleDetails = () => {
                      setExpandedDetails(expandedDetails === idx ? null : idx);
                    };

                    return (
                      <Box
                        key={idx}
                        display="flex"
                        flexDirection="column"
                        alignItems={
                          msg.sender === "user" ? "flex-end" : "flex-start"
                        }
                        mb={1}
                      >
                        <StyledText
                          variant="body2"
                          sx={{
                            bgcolor:
                              msg.sender === "user" ? "#d1e7dd" : "#f0f0f0",
                            cursor: hasDetails ? "pointer" : "default",
                          }}
                        >
                          {msg.text}
                          {msg.sender === "bot" && hasDetails && (
                            <StyledButton
                              variant="text"
                              size="small"
                              color="info"
                              onClick={toggleDetails}
                            >
                              {expandedDetails === idx
                                ? "Hide Details"
                                : "Details"}
                            </StyledButton>
                          )}
                        </StyledText>

                        {/* Details box */}
                        {expandedDetails === idx && hasDetails && (
                          <DetailBox>
                            {msg.references.map((ref, i) => {
                              const lines = ref.content.text.split(/\\n|\n/);
                              return (
                                <Box key={i} mb={2}>
                                  {lines.map((line, idx) => {
                                    const columns = line.split(/\t+/);
                                    return (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          borderBottom: "1px solid #eee",
                                          paddingY: 0.3,
                                        }}
                                      >
                                        {columns.map((col, j) => (
                                          <Box
                                            key={j}
                                            sx={{
                                              flex:
                                                j === columns.length - 1
                                                  ? 2
                                                  : 1,
                                              fontSize: 14,
                                            }}
                                          >
                                            {col.trim()}
                                          </Box>
                                        ))}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              );
                            })}
                          </DetailBox>
                        )}
                      </Box>
                    );
                  })}

                  {loading && (
                    <Box display="flex" justifyContent="flex-start" mb={1}>
                      <LoadingContainer>
                        <TypingIndicator />
                      </LoadingContainer>
                    </Box>
                  )}
                </MessageListContainer>

                {/* Input */}
                <Box display="flex" gap={1}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Type your message"
                    fullWidth
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={sendMessage}
                    disabled={loading}
                  >
                    Send
                  </Button>
                </Box>
              </StyledPaper>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return null;
};

export default ChatBot;
